import { RobotTypeEnum } from 'api/schema'
import clsx from 'clsx'
import { format } from 'date-fns'
import { ReactComponent as BatteryChargingSvg } from 'images/icons/battery-charging.svg'
import { ReactComponent as BatterySvg } from 'images/icons/battery_half.svg'
import { ReactComponent as CleanWater } from 'images/icons/clean_drop.svg'
import { ReactComponent as DirtyWater } from 'images/icons/dirty_drop.svg'
import { ReactComponent as EmergencySvg } from 'images/icons/emergency.svg'
import { ReactComponent as GarbageBin } from 'images/icons/garbage.svg'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { ReactComponent as JoystickSvg } from 'images/icons/joystick.svg'
import { ReactComponent as PauseSvg } from 'images/icons/pause.svg'
import { ReactComponent as NoConnectionSvg } from 'images/icons/signal_wifi_off.svg'
import { ReactComponent as ConnectionSvg } from 'images/icons/signal_wifi_on.svg'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot, selectRobotTelemetry } from 'store/selectors/robot'
import { ROBOT_COMMANDS, ROBOT_MODES, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { createDiagnosticStatusClassGetter, getDiagnosticStatusText } from 'utils/diagnosticsUtils'

import style from '../RobotDetails.module.css'

const RobotInfo = () => {
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const robot = useSelector(selectRobot)

  const getTextStatus = (percents: number | undefined | null) => {
    if (!percents && percents !== 0) return '-'
    if (percents > 60) return t('robot.good')
    else if (percents > 30) return t('robot.normal')
    else return t('robot.low')
  }

  const getBatteryStatus = (
    charging: boolean | null | undefined,
    power: number | null | undefined,
    current: number | null | undefined
  ) => {
    if (charging === true) {
      const currentText = formatBatteryCurrent(current, charging)
      return `Charging${currentText}`
    }
    return getTextStatus(power)
  }

  const getBatteryStatusClass = (charging: boolean | null | undefined, power: number | null | undefined) => {
    if (charging === true) return style.batteryCharging
    if (!power && power !== 0) return style.locked
    if (power > 70) return style.batteryGood
    if (power > 30) return style.batteryNormal
    return style.batteryLow
  }

  const formatBatteryCurrent = (current: number | null | undefined, charging: boolean | null | undefined) => {
    if (charging !== true) return ''
    if (current === null || current === undefined || current === 0) return ''
    const sign = current >= 0 ? '+' : ''
    return ` ${sign}${current.toFixed(2)}A`
  }

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE

  const getRobotBatteryPower = () => {
    if (telemetry?.robotBattery?.power !== undefined && telemetry?.robotBattery?.power !== null) {
      return telemetry.robotBattery.power
    }
    if (telemetry?.batteryPower !== undefined && telemetry?.batteryPower !== null) {
      return telemetry.batteryPower
    }
    return null
  }

  const getRobotBatteryCharging = () => {
    return telemetry?.robotBattery?.charging ?? null
  }

  const hasRobotBatteryData = () => {
    const power = getRobotBatteryPower()
    return power !== null && power !== undefined
  }

  const hasJoystickBatteryData = () => {
    return telemetry?.joystickBattery?.power !== undefined && telemetry?.joystickBattery?.power !== null
  }

  // Get system diagnostics from WebSocket telemetry or fallback to RobotRead from API
  const getSystemDiagnostics = () => {
    // Prefer WebSocket data (more real-time)
    if (telemetry?.system_diagnostics !== null && telemetry?.system_diagnostics !== undefined) {
      return telemetry.system_diagnostics
    }
    // Fallback to API data (from RobotRead)
    if (
      robot.robotInfo?.robotRaw?.system_diagnostics !== null &&
      robot.robotInfo?.robotRaw?.system_diagnostics !== undefined
    ) {
      return robot.robotInfo.robotRaw.system_diagnostics
    }
    return null
  }

  const getDiagnosticStatusClass = createDiagnosticStatusClassGetter(style, style.locked)

  const robotBatteryPower = getRobotBatteryPower()
  const robotBatteryCharging = getRobotBatteryCharging()
  const joystickBattery = telemetry?.joystickBattery
  const systemDiagnostics = getSystemDiagnostics()
  const isEmergencyButtonPressed = telemetry?.locks?.danger_button === true

  return (
    <div className={style.root}>
      <div className={clsx(style.tableRow, !isOnline && style.offlineRow)}>
        <div>
          {isOnline ? (
            <ConnectionSvg className={style.connectionIcon} />
          ) : (
            <NoConnectionSvg className={style.connectionIcon} />
          )}
        </div>
        <div className={style.textAlignLeft}>Connection</div>
        <div className={style.centered}>{isOnline ? 'Online' : 'Offline'}</div>
        {isOnline && (
          <div>
            <div className={style.percents}>
              {telemetry?.timestamp ? format(new Date(telemetry.timestamp * 1000), 'dd LLLL yyyy') : '-'}
            </div>
            <div className={style.percents}>
              {telemetry?.velocity !== undefined && `Spd: ${Math.round(telemetry.velocity * 10) / 10}m/s `}
              {telemetry?.timestamp && format(new Date(telemetry.timestamp * 1000), 'HH:mm:ss')}
            </div>
          </div>
        )}
      </div>
      {isOnline && (
        <>
          {systemDiagnostics && (
            <div
              className={clsx(style.tableRow, {
                [style.errorRow]: systemDiagnostics.level >= 2,
                [style.warningRow]: systemDiagnostics.level === 1,
              })}
            >
              <div>
                <GearSvg />
              </div>
              <div className={style.textAlignLeft}>Diagnostics</div>
              <div className={style.centered}>{getDiagnosticStatusText(systemDiagnostics.level)}</div>
              <div className={style.percents}>{systemDiagnostics.message || '-'}</div>
            </div>
          )}
          {isEmergencyButtonPressed && (
            <div className={clsx(style.tableRow, style.errorRow)}>
              <div>
                <EmergencySvg className={style.emergencyIcon} />
              </div>
              <div className={style.textAlignLeft}>Emergency</div>
              <div className={style.centered}>Locked</div>
              <div className={style.percents}>-</div>
            </div>
          )}
          {robot.robotInfo?.activeCommand === ROBOT_COMMANDS.PAUSE &&
            robot.robotInfo?.activeMode === ROBOT_MODES.MODE_AUTOMATIC && (
              <div className={clsx(style.tableRow, style.warningRow)}>
                <div>
                  <PauseSvg className={style.pauseIcon} style={{ color: '#4680fdff' }} />
                </div>
                <div className={style.textAlignLeft}>Pause</div>
                <div className={style.centered}>Activated</div>
                <div className={style.percents}></div>
              </div>
            )}
          {hasRobotBatteryData() && (
            <div
              className={clsx(style.tableRow, {
                [style.batteryCharging]: robotBatteryCharging,
                [style.errorRow]: !robotBatteryCharging && robotBatteryPower !== null && robotBatteryPower <= 30,
              })}
            >
              <div>{robotBatteryCharging ? <BatteryChargingSvg /> : <BatterySvg />}</div>
              <div className={style.textAlignLeft}>Battery</div>
              <div className={style.centered}>
                {getBatteryStatus(robotBatteryCharging, robotBatteryPower, telemetry?.robotBattery?.current)}
              </div>
              <div className={style.percents}>
                {robotBatteryPower !== null && robotBatteryPower !== undefined
                  ? `${Math.round(robotBatteryPower)}%`
                  : '-'}
              </div>
            </div>
          )}
          {hasJoystickBatteryData() && (
            <div
              className={clsx(style.tableRow, {
                [style.batteryCharging]: joystickBattery?.charging,
                [style.errorRow]:
                  !joystickBattery?.charging && joystickBattery?.power !== null && (joystickBattery?.power ?? 0) <= 30,
              })}
            >
              <div className={style.joystickIconContainer}>
                <JoystickSvg className={style.joystickIcon} />
              </div>
              <div className={style.textAlignLeft}>Joystick battery</div>
              <div className={style.centered}>
                {getBatteryStatus(joystickBattery?.charging, joystickBattery?.power, joystickBattery?.current)}
              </div>
              <div className={style.percents}>
                {joystickBattery?.power !== null && joystickBattery?.power !== undefined
                  ? `${Math.round(joystickBattery.power)}%`
                  : '-'}
              </div>
            </div>
          )}
          <div
            className={clsx(
              style.tableRow,
              telemetry?.waterTankFilling !== undefined &&
                telemetry?.waterTankFilling !== null &&
                telemetry.waterTankFilling < 10 &&
                style.errorRow
            )}
          >
            <div>
              <CleanWater />
            </div>
            <div className={style.textAlignLeft}>Clean water</div>
            <div className={style.centered}>{getTextStatus(telemetry?.waterTankFilling)}</div>
            <div className={style.percents}>
              {telemetry?.waterTankFilling !== undefined && telemetry?.waterTankFilling !== null
                ? `${Math.round(telemetry.waterTankFilling)}%`
                : '-'}
            </div>
          </div>
          {robot.robotInfo?.robot_type !== RobotTypeEnum.SWEEPER &&
            telemetry?.dirtyWaterTankFilling !== null &&
            telemetry?.dirtyWaterTankFilling !== undefined && (
              <div
                className={clsx(
                  style.tableRow,
                  telemetry.dirtyWaterTankFilling !== undefined &&
                    telemetry.dirtyWaterTankFilling !== null &&
                    telemetry.dirtyWaterTankFilling > 90 && // show red if dirty water tank is full
                    style.errorRow
                )}
              >
                <div>
                  <DirtyWater />
                </div>
                <div className={style.textAlignLeft}>Dirty water</div>
                <div className={style.centered}>
                  {telemetry?.dirtyWaterTankFilling !== undefined &&
                  telemetry?.dirtyWaterTankFilling !== null &&
                  telemetry.dirtyWaterTankFilling > 70
                    ? t('robot.full')
                    : getTextStatus(telemetry?.dirtyWaterTankFilling)}
                </div>
                <div className={style.percents}>
                  {telemetry?.dirtyWaterTankFilling !== undefined
                    ? `${Math.round(telemetry.dirtyWaterTankFilling)}%`
                    : '-'}
                </div>
              </div>
            )}
          {/* Garbage/Waste tank will be shown if data is available */}
          {telemetry?.trashFullness !== null && telemetry?.trashFullness !== undefined && (
            <div className={style.tableRow}>
              <div>
                <GarbageBin />
              </div>
              <div className={style.textAlignLeft}>Waste tank</div>
              <div className={style.centered}>{getTextStatus(telemetry?.trashFullness)}</div>
              <div className={style.percents}>
                {telemetry?.trashFullness !== undefined ? `${Math.round(telemetry.trashFullness)}%` : '-'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default RobotInfo
